import classNames from "classnames";
import React, { Children, FC, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
// @ts-ignore
import Swipe, { ReactEasySwipeProps } from "react-easy-swipe";
import { useInterval } from "../../hooks/use-interval";
import { normalizeArrowKey } from "../../utils/date-utils";
import { toCSSTranslate3dFunctionString } from "../../utils/dom-utils";
import { IconButton } from "../button";
import { IconNavArrowLeft, IconNavArrowRight } from "../icons";
import { CSS_CLASSES } from "./carousel-css-classes";
import { CarouselArrowRenderer, CarouselImageProps, CarouselIndicatorRenderer, CarouselProps } from "./carousel.types";

const SWIPE_SCROLL_TO_TOLERANCE = 5;
const defaultLeftArrowRenderer: CarouselArrowRenderer = (color, onClick, hasPrev, label) => (
  <IconButton
    color={color}
    icon={<IconNavArrowLeft />}
    variant="ghost"
    label={label}
    className={classNames(CSS_CLASSES.CAROUSEL_ARROW, CSS_CLASSES.CAROUSEL_ARROW_LEFT)}
    onClick={onClick}
    size="small"
    disabled={!hasPrev}
  />
);

const defaultRightArrowRenderer: CarouselArrowRenderer = (color, onClick, hasNext, label) => (
  <IconButton
    color={color}
    icon={<IconNavArrowRight />}
    variant="ghost"
    label={label}
    className={classNames(CSS_CLASSES.CAROUSEL_ARROW, CSS_CLASSES.CAROUSEL_ARROW_RIGHT)}
    onClick={onClick}
    size="small"
    disabled={!hasNext}
  />
);

const defaultIndicatorRenderer: CarouselIndicatorRenderer = (onClick, selected, index, label) => {
  const dotClasses = classNames([CSS_CLASSES.CAROUSEL_DOT], {
    [CSS_CLASSES.CAROUSEL_DOT_SELECTED]: selected,
  });
  return (
    <li
      className={dotClasses}
      onClick={onClick}
      onKeyDown={onClick}
      value={index}
      key={index}
      role="button"
      tabIndex={0}
      aria-label={`${label} ${index + 1}`}
    />
  );
};

export interface Position {
  oldPosition: number;
  lastPosition: number;
  position: number;
}

export type PropsFromCarousel = Omit<CarouselProps, "selectedSlide">;

export interface UnControlledCarouselOwnProps {
  selectedSlide: number;
}

export type UnControlledCarouselProps = PropsFromCarousel & UnControlledCarouselOwnProps;

export const UnControlledCarousel: FC<UnControlledCarouselProps> = ({
  color = "interactive",
  autoPlay = false,
  loop: infiniteLoop = false,
  interval = 1000,
  transitionDuration = 350,
  showArrows = true,
  showIndicators = true,
  aspectRatio = {
    width: 16,
    height: 9,
  },
  showFavorite = false,
  showBackground = false,
  leftArrowRenderer = defaultLeftArrowRenderer,
  rightArrowRenderer = defaultRightArrowRenderer,
  indicatorRenderer = defaultIndicatorRenderer,
  selectedSlide,
  onChange = () => {},
  leftArrowLabel = "previous slide / item",
  rightArrowLabel = "next slide / item",
  indicatorLabel: itemLabel = "slide item",
  allowMouseEvents,
  pauseOnHover,
  className,
  children,
}) => {
  const [isMouseEntered, setIsMouseEntered] = useState(false);
  const [swiping, setSwiping] = useState(false);

  const infoRef = useRef<Position>({
    oldPosition: -1,
    position: -1,
    lastPosition: -1,
  });

  const carouselWrapperRef = useRef<HTMLDivElement>(null);
  let listRef = useRef<HTMLUListElement>();
  /**Start Handlers */
  const moveTo = useCallback(
    (position?: number, fromSwipe?: boolean) => {
      if (typeof position !== "number") {
        return;
      }
      const lastPosition = Children.count(children) - 1;

      const oldPosition = position;
      const needClonedSlide = infiniteLoop && !fromSwipe && (position < 0 || position > lastPosition);
      if (position < 0) {
        position = infiniteLoop ? lastPosition : 0;
      }

      if (position > lastPosition) {
        position = infiniteLoop ? 0 : lastPosition;
      }
      if (needClonedSlide) {
        infoRef.current.oldPosition = oldPosition;
        infoRef.current.lastPosition = lastPosition;
        infoRef.current.position = position;
        setSwiping(true);
      } else {
        onChange(position);
      }
    },
    [children, infiniteLoop, onChange]
  );

  const decrement = useCallback(
    (positions = 1, fromSwipe = false) => {
      moveTo(selectedSlide - (typeof positions === "number" ? positions : 1), fromSwipe);
    },
    [moveTo, selectedSlide]
  );

  const increment = useCallback(
    (positions = 1, fromSwipe = false) => {
      moveTo(selectedSlide + (typeof positions === "number" ? positions : 1), fromSwipe);
    },
    [moveTo, selectedSlide]
  );

  const handleAutoPlay = useCallback(() => {
    increment();
  }, [increment]);

  const onClickNext = useCallback(() => {
    increment(1, false);
  }, [increment]);

  const onClickPrev = useCallback(() => {
    decrement(1, false);
  }, [decrement]);

  const onSwipeForward = useCallback(() => {
    increment(1, true);
  }, [increment]);

  const onSwipeBackwards = useCallback(() => {
    decrement(1, true);
  }, [decrement]);

  const changeItem = useCallback(
    (newIndex: number) => (e: React.MouseEvent | React.KeyboardEvent) => {
      onChange(newIndex);
    },
    [onChange]
  );

  const handleAutoPlayStop = useCallback(() => {
    setIsMouseEntered(true);
  }, []);
  const handleAutoPlayStart = useCallback(() => {
    setIsMouseEntered(false);
  }, []);

  const setPosition = useCallback((position: number, forceReflow?: boolean) => {
    if (listRef.current instanceof HTMLElement) {
      ["WebkitTransform", "MozTransform", "MsTransform", "OTransform", "transform", "msTransform"].forEach((prop) => {
        if (listRef.current) {
          listRef.current.style[prop as any] = toCSSTranslate3dFunctionString({ value: position, unit: "%" });
        }
      });

      if (forceReflow) {
        if (listRef.current) {
          return listRef.current.offsetLeft;
        }
      }
    }
  }, []);
  /**End Handlers */

  /**Side effects */
  useEffect(() => {
    if (pauseOnHover && carouselWrapperRef && carouselWrapperRef.current) {
      carouselWrapperRef.current.addEventListener("mouseenter", handleAutoPlayStop);
      carouselWrapperRef.current.addEventListener("mouseleave", handleAutoPlayStart);
    }
  }, [handleAutoPlayStart, handleAutoPlayStop, pauseOnHover]);

  useEffect(() => {
    if (swiping) {
      const { oldPosition, lastPosition, position } = infoRef.current;
      if (oldPosition < 0) {
        setPosition(-(lastPosition + 2) * 100, true);
      } else if (oldPosition > lastPosition) {
        setPosition(0, true);
      }

      onChange(position);
      setSwiping(false);
    }
  }, [onChange, setPosition, swiping]);

  const keyDownHandlers: { [key: string]: any } = useMemo(
    () => ({
      ArrowLeft(event: KeyboardEvent) {
        event.preventDefault();
        decrement();
      },
      ArrowRight(event: KeyboardEvent) {
        event.preventDefault();
        increment();
      },
    }),
    [decrement, increment]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const key = normalizeArrowKey(event as any);
      if (key && keyDownHandlers[key]) {
        keyDownHandlers[key](event);
      }
    },
    [keyDownHandlers]
  );
  /**Render helpers */
  useInterval(handleAutoPlay, !autoPlay || (pauseOnHover && isMouseEntered) ? null : interval);

  const getPosition = (index: number) => {
    if (infiniteLoop) {
      // index has to be added by 1 because of the first cloned slide
      ++index;
    }

    if (index === 0) {
      return 0;
    }

    return -index * 100;
  };

  /**Rendering  */
  if (!children || Children.count(children) === 0) {
    return null;
  }

  const paddingBottom = `${(aspectRatio.height / aspectRatio.width) * 100}%`;

  const slideUI = Children.toArray(children).map((child, index) => {
    const slideClasses = classNames({
      [CSS_CLASSES.CAROUSEL_SLIDE]: true,
      [CSS_CLASSES.CAROUSEL_SLIDE_SELECTED]: selectedSlide === index,
    });

    return (
      <li className={slideClasses} key={index}>
        {React.isValidElement<CarouselImageProps>(child)
          ? React.cloneElement(child, {
              ...child.props,
              showFavorite: child.props.showFavorite !== undefined ? child.props.showFavorite : showFavorite,
            })
          : child}
      </li>
    );
  });

  const canShowArrows = showArrows && slideUI.length > 1;
  const hasPrev = (canShowArrows && (selectedSlide > 0 || infiniteLoop)) || false;
  const hasNext = (canShowArrows && (selectedSlide < slideUI.length - 1 || infiniteLoop)) || false;

  const currentPosition = getPosition(selectedSlide);

  // if 3d is available, let's take advantage of the performance of transform
  const transformProp = toCSSTranslate3dFunctionString({ value: currentPosition, unit: "%" });
  const transitionTimeInMs = transitionDuration + "ms";
  let itemListStyles: any = {
    WebkitTransform: transformProp,
    MozTransform: transformProp,
    MsTransform: transformProp,
    OTransform: transformProp,
    transform: transformProp,
    msTransform: transformProp,
  };

  if (!swiping) {
    itemListStyles = {
      ...itemListStyles,
      WebkitTransitionDuration: transitionTimeInMs,
      MozTransitionDuration: transitionTimeInMs,
      MsTransitionDuration: transitionTimeInMs,
      OTransitionDuration: transitionTimeInMs,
      transitionDuration: transitionTimeInMs,
      msTransitionDuration: transitionTimeInMs,
    };
  }

  let swiperProps: ReactEasySwipeProps = {
    className: classNames({ [CSS_CLASSES.CAROUSEL_CONTENT]: true, swiping: swiping }),
    style: itemListStyles,
    tolerance: SWIPE_SCROLL_TO_TOLERANCE,
    onSwipeLeft: onSwipeForward,
    onSwipeRight: onSwipeBackwards,
  };

  const containerWrapperClass = classNames({
    [CSS_CLASSES.CAROUSEL_CONTENT_ROOT]: true,
    [CSS_CLASSES.CAROUSEL_BG_COLOR]: showBackground,
  });

  const containerRoot = classNames(
    CSS_CLASSES.CAROUSEL_ROOT,
    {
      [CSS_CLASSES.CAROUSEL_COLOR_INTERACTIVE]: color === "interactive",
      [CSS_CLASSES.CAROUSEL_COLOR_PRIMARY]: color === "primary",
    },
    className
  );

  return (
    <div className={containerRoot} ref={carouselWrapperRef} onKeyDown={handleKeyDown} tabIndex={-1}>
      <div className={CSS_CLASSES.CAROUSEL_SLIDES_SECTION}>
        <div className={CSS_CLASSES.CAROUSEL_ARROW_SECTION}>
          {showArrows && leftArrowRenderer(color, onClickPrev, hasPrev, leftArrowLabel)}
        </div>
        <div className={CSS_CLASSES.CAROUSEL_SLIDES}>
          <div className={CSS_CLASSES.CAROUSEL_CONTENT_SECTION} style={{ paddingBottom }}>
            <div className={containerWrapperClass}>
              <div className={CSS_CLASSES.CAROUSEL_CONTENT}>
                <Swipe
                  tagName="ul"
                  innerRef={(node: HTMLUListElement) => (listRef.current = node)}
                  {...swiperProps}
                  allowMouseEvents={allowMouseEvents}
                >
                  {infiniteLoop && slideUI[0]}
                  {slideUI}
                  {infiniteLoop && slideUI[slideUI.length - 1]}
                </Swipe>
              </div>
            </div>
          </div>
        </div>
        <div className={CSS_CLASSES.CAROUSEL_ARROW_SECTION}>
          {showArrows && rightArrowRenderer(color, onClickNext, hasNext, rightArrowLabel)}
        </div>
      </div>
      {showIndicators ? (
        <ul className={CSS_CLASSES.CAROUSEL_DOTS}>
          {Array.from(Array(slideUI.length).keys()).map((index) =>
            indicatorRenderer(changeItem(index), index === selectedSlide, index, itemLabel)
          )}
        </ul>
      ) : null}
    </div>
  );
};
